import { create } from "zustand";
import {
  imageService,
  ImageItem,
  UploadImageDto,
  CreateImageDto,
  UpdateImageDto,
} from "@/services/imageService";
import { showToast } from "@/lib/toast";

interface ImageState {
  items: ImageItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  isUploading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  selected: ImageItem | null;
  error: string | null;

  fetch: (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }) => Promise<void>;
  fetchMore: (params?: { search?: string }) => Promise<void>;
  upload: (file: File, data?: UploadImageDto) => Promise<boolean>;
  createFromUrl: (data: CreateImageDto) => Promise<boolean>;
  update: (id: string, data: UpdateImageDto) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  setSelected: (item: ImageItem | null) => void;
  clearError: () => void;
}

export const useImageStore = create<ImageState>()((set, get) => ({
  items: [],
  pagination: { page: 1, limit: 24, total: 0, totalPages: 0 },
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
  isUploading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  selected: null,
  error: null,

  fetch: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await imageService.list({
        page: params?.page ?? get().pagination.page,
        limit: params?.limit ?? get().pagination.limit,
        search: params?.search,
      });
      if (res.ok && res.data) {
        const data: any = (res as any).data?.data?.data ?? (res as any).data;
        console.log("data: ", data);
        const raw = data.pagination ?? get().pagination;
        const pagination = {
          page: Number(raw.page) || 1,
          limit: Number(raw.limit) || 24,
          total: Number(raw.total) || 0,
          totalPages: Number(raw.totalPages) || 0,
        };
        // fetch luôn đặt lại items theo kết quả API (không nối)
        set({
          items: data.images ?? [],
          pagination,
          isLoading: false,
          hasMore: pagination.page * pagination.limit < pagination.total,
        });
      } else {
        set({ isLoading: false, error: "Không thể tải danh sách ảnh" });
        showToast.error("Lỗi", "Không thể tải danh sách ảnh");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Lỗi khi tải danh sách ảnh";
      set({ isLoading: false, error: msg });
      showToast.error("Lỗi", msg);
    }
  },

  fetchMore: async (params) => {
    const { pagination, items } = get();
    if (get().isLoadingMore) return;
    const nextPage = Number(pagination.page) + 1;
    if (
      nextPage > Number(pagination.totalPages) &&
      Number(pagination.totalPages) !== 0
    ) {
      set({ hasMore: false });
      return;
    }
    set({ isLoadingMore: true });
    try {
      const res = await imageService.list({
        page: nextPage,
        limit: pagination.limit,
        search: params?.search,
      });
      if (res.ok && res.data) {
        const data: any = (res as any).data?.data?.data ?? (res as any).data;
        const r = data.pagination ?? pagination;
        const newPagination = {
          page: Number(r.page) || pagination.page,
          limit: Number(r.limit) || pagination.limit,
          total: Number(r.total) || pagination.total,
          totalPages: Number(r.totalPages) || pagination.totalPages,
        };
        // fetchMore nối thêm vào cuối danh sách hiện có
        const newItems = [...items, ...(data.images ?? [])];
        set({
          items: newItems,
          pagination: newPagination,
          isLoadingMore: false,
          hasMore:
            newPagination.page * newPagination.limit < newPagination.total,
        });
      } else {
        set({ isLoadingMore: false });
      }
    } catch (e) {
      set({ isLoadingMore: false });
    }
  },

  upload: async (file, data) => {
    set({ isUploading: true, error: null });
    try {
      const res = await imageService.upload(file, data);
      if (res.ok && res.data) {
        const created: any = (res as any).data?.data ?? (res as any).data;
        const { items, pagination } = get();
        const newTotal = Number(pagination.total) + 1;
        const newTotalPages = Math.ceil(
          newTotal / Number(pagination.limit || 1)
        );
        const newItems = [created, ...items];
        set({
          items: newItems,
          pagination: {
            ...pagination,
            total: newTotal,
            totalPages: newTotalPages,
          },
          isUploading: false,
        });
        showToast.success("Thành công", "Upload ảnh thành công");
        return true;
      }
      set({ isUploading: false, error: "Upload ảnh thất bại" });
      showToast.error("Lỗi", "Upload ảnh thất bại");
      return false;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Lỗi khi upload ảnh";
      set({ isUploading: false, error: msg });
      showToast.error("Lỗi", msg);
      return false;
    }
  },

  createFromUrl: async (data) => {
    set({ isCreating: true, error: null });
    try {
      const res = await imageService.createFromUrl(data);
      if (res.ok && res.data) {
        const created: any = (res as any).data?.data ?? (res as any).data;
        const { items, pagination } = get();
        const newTotal = Number(pagination.total) + 1;
        const newTotalPages = Math.ceil(
          newTotal / Number(pagination.limit || 1)
        );
        const newItems = [created, ...items];
        set({
          items: newItems,
          pagination: {
            ...pagination,
            total: newTotal,
            totalPages: newTotalPages,
          },
          isCreating: false,
        });
        showToast.success("Thành công", "Tạo ảnh từ URL thành công");
        return true;
      }
      set({ isCreating: false, error: "Tạo ảnh từ URL thất bại" });
      showToast.error("Lỗi", "Tạo ảnh từ URL thất bại");
      return false;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Lỗi khi tạo ảnh từ URL";
      set({ isCreating: false, error: msg });
      showToast.error("Lỗi", msg);
      return false;
    }
  },

  update: async (id, data) => {
    set({ isUpdating: true, error: null });
    try {
      const res = await imageService.update(id, data);
      if (res.ok && res.data) {
        const updated: any = (res as any).data?.data ?? (res as any).data;
        const { items } = get();
        const newItems = items.map((it) => (it._id === id ? updated : it));
        set({ items: newItems, isUpdating: false });
        showToast.success("Thành công", "Cập nhật ảnh thành công");
        return true;
      }
      set({ isUpdating: false, error: "Cập nhật ảnh thất bại" });
      showToast.error("Lỗi", "Cập nhật ảnh thất bại");
      return false;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Lỗi khi cập nhật ảnh";
      set({ isUpdating: false, error: msg });
      showToast.error("Lỗi", msg);
      return false;
    }
  },

  remove: async (id) => {
    set({ isDeleting: true, error: null });
    try {
      const res = await imageService.remove(id);
      if (res.ok) {
        // Cập nhật lạc quan tại chỗ để giữ nguyên vị trí người dùng
        const { items, pagination } = get();
        // Xóa item nhưng vẫn giữ tất cả item đã load (trang 1..N)
        const newItems = items.filter((it) => it._id !== id);
        const newTotal = Math.max(0, Number(pagination.total) - 1);
        const newTotalPages = Math.ceil(
          newTotal / Number(pagination.limit || 1)
        );
        const currentPage = Number(pagination.page);
        const limit = Number(pagination.limit);
        const hasMore = currentPage * limit < newTotal;
        set({
          items: newItems,
          pagination: {
            ...pagination,
            total: newTotal,
            totalPages: newTotalPages,
          },
          isDeleting: false,
          hasMore,
        });
        // Nếu còn trang sau và thiếu item, lấp từ trang kế tiếp nhưng GIỮ nguyên các item đầu
        if (hasMore && newItems.length < currentPage * limit) {
          await get().fetchMore({});
        }
        showToast.success("Thành công", "Xóa ảnh thành công");
        return true;
      }
      set({ isDeleting: false, error: "Xóa ảnh thất bại" });
      showToast.error("Lỗi", "Xóa ảnh thất bại");
      return false;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Lỗi khi xóa ảnh";
      set({ isDeleting: false, error: msg });
      showToast.error("Lỗi", msg);
      return false;
    }
  },

  setSelected: (item) => set({ selected: item }),
  clearError: () => set({ error: null }),
}));
